/**
 * Toast notifications — re-export Sonner z brandingiem Imperial + wymuszeniem
 * standardu komunikatów (zob. `feedback_messages_system_standard` memory).
 *
 * **Single import point:** wszystkie toasts w aplikacji idą przez ten plik.
 * Pozwala to centralnie zmieniać domyślny config (duration, position, theme)
 * + ujednolicać język (PL placeholders/labels).
 *
 * **Standard komunikatów (system-wide):**
 *   - **Errors -> PERSISTENT** (`toast.error` domyślnie `duration: Infinity`).
 *     User MUSI kliknąć ✕ żeby zamknąć — nie może przegapić błędu.
 *     Można override per-call (`toast.error(msg, { duration: 4000 })`).
 *   - **Success / info / warning -> TRANSIENT** (default 4s z Toaster props).
 *     Routine notifications mogą zniknąć — user widzi i odpuszcza.
 *   - **Inline persistent (in-form / in-panel) -> `<FormStatus>`**, nie toast.
 *     FormStatus jest UNIWERSALNY (visitor + admin) — single source of truth
 *     dla bannerów stanu w obrębie komponentu.
 *
 * @see https://sonner.emilkowal.ski/ — official docs
 * @see ../../components/ui/Form/FormStatus.tsx — inline banner (alternatywa do toast)
 * @see ../../components/ui/ConfirmDialog dla blocking confirms (NIE toast)
 *
 * **Kiedy używać toast vs FormStatus vs confirmDialog:**
 *   - `toast.success/info/warning` — non-blocking global notification (top-right),
 *     po routine action ("Zapisano"). Auto-dismiss 4s.
 *   - `toast.error` — non-blocking ALE persistent. Global error wymagający
 *     user-action (kliknięcie ✕). Override duration tylko gdy świadomie OK.
 *   - `<FormStatus>` — inline banner w obrębie formularza/panelu. Persistent
 *     zawsze, opcjonalny `dismissible` prop z ✕. Używaj dla per-form errors
 *     (validation, server reject) i in-place sukcesów.
 *   - `confirmDialog()` — blocking decyzja krytyczna ("Czy na pewno usunąć?"),
 *     czeka na odpowiedź, brak utraty danych jeśli user cancel.
 */

import { toast as sonnerToast, type ExternalToast } from "sonner";

/* Pre-bind original .error żeby uniknąć recursion przy reassign. */
const _origError = sonnerToast.error.bind(sonnerToast);

/* Override .error — persistent by default (zgodnie ze standardem komunikatów).
   Per-call override przez `{ duration: 4000 }` itp. wciąż działa — własne
   options nadpisują naszych defaults przez spread. */
sonnerToast.error = ((message: Parameters<typeof _origError>[0], options?: ExternalToast) =>
  _origError(message, { duration: Infinity, ...options })) as typeof sonnerToast.error;

export { sonnerToast as toast };
export type { ExternalToast } from "sonner";

/**
 * Domyślne opcje dla różnych typów toastów — wystawiamy jako helpers
 * żeby nie powielać konfiguracji w każdym call site.
 */
export const TOAST_DURATION = {
  short: 2500,
  medium: 4000,    //default dla success/info/warning
  long: 8000,
  persistent: Infinity,  //default dla errors (po naszym wrapperze powyżej)
} as const;
